import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  ButtonGroup,
  Button,
  Autocomplete,
  FormControl,
  FormLabel,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Divider,
  Checkbox,
  FormControlLabel,
  IconButton,
  Paper,
  Fade,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import {
  CommonSubHeader,
  useLocationHelpers,
} from "../../Components/helpers/Helpers";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";

import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DragOverlay } from "@dnd-kit/core";
import { UserContext } from "../../Components/Context/Context";
import { questionSwal, successSwal, warningSwal } from "../../utils/swal";
import {
  ArrowBackIosNew,
  ArrowForward,
  ArrowForwardIos,
} from "@mui/icons-material";
import { CustomButtonPlusIcon } from "../../Components/Common/Design/CustomButtonPlusIcon";
import { Save } from "lucide-react";

/* =========================
   SECCIONES BASE
========================= */
const initialSections = [
  { id: "firma", title: "Firma", fixed: true, selected: false },
  { id: "documento", title: "Documento", fixed: true, selected: false },
  { id: "revision", title: "Revisión", fixed: true, selected: false },
  { id: "formulario", title: "Formulario FI", fixed: true, selected: false },
];

/* =========================
   ITEM DRAGGABLE
========================= */
const SortableSection = ({ section, index, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    borderRadius: 10,
    background: "#ffffff",
    boxShadow: isDragging
      ? "0 0 0 rgba(0,0,0,0)"
      : "0 2px 6px rgba(0,0,0,0.05)",
    opacity: isDragging ? 0 : 1,
    display: "flex",
    justifyContent: "space-between",
    cursor: isDragging ? "grabbing" : "grab",
    width: "100%",
  };

  return (
    <div ref={setNodeRef} style={style}>
      {/* DRAG HANDLE */}
      <Tooltip
        title={section?.title || ""}
        placement="top"
        arrow
        disableHoverListener={!(section?.title?.length > 40)}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          padding={"10px 15px"}
          width={"100%"}
          overflow="hidden"
          {...attributes}
          {...listeners}>
          <Typography
            fontWeight={600}
            color="#0d47a1"
            sx={{
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              maxWidth: "100%",
              cursor: section?.title?.length > 200 ? "pointer" : "default",
            }}>
            {index + 1} .{" "}
            {section?.title?.length > 200
              ? section.title.slice(0, 200) + "..."
              : section.title}
          </Typography>
        </Box>
      </Tooltip>

      {/* DELETE */}
      <IconButton
        size="small"
        sx={{
          color: "#d32f2f",
          "&:hover": { backgroundColor: "rgba(211,47,47,0.1)" },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(section);
        }}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </div>
  );
};

/* =========================
   MAIN
========================= */
const Crear = () => {
  const navigate = useNavigate();
  const { goPanel, goTable } = useLocationHelpers();
  const { userData } = useContext(UserContext);
  const { user, userCompany } = userData;

  const [activeStep, setActiveStep] = useState(0);
  const [companies, setCompanies] = useState([]);
  const [sections, setSections] = useState(initialSections);
  const [workflow, setWorkflow] = useState([]);
  const [newSectionName, setNewSectionName] = useState("");
  const [activeId, setActiveId] = useState(null);

  const [caseTypeData, setCaseTypeData] = useState({
    // company: userCompany || null,
    company: null,
    case_type: "",
    case_form: "",
  });

  const steps = ["Información básica", "Flujo de trabajo"];

  const getData = async () => {
    const { data } = await api.post(
      `${process.env.REACT_APP_API_URL}/casestypes/create`,
    );

    console.log(data);

    const companiesWithDefault = [
      { id: null, company_name: "Seleccionar" },
      ...data?.companies_list,
    ];

    const casesTypeWithDefault = [
      { id: null, case_type: "Seleccionar" },
      ...data?.cases_types_list,
    ];

    setCompanies(companiesWithDefault || []);
  };

  useEffect(() => {
    getData();
  }, []);

  const handleFieldChange = (name, value) => {
    setCaseTypeData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleMainSection = (sectionId) => {
    setSections((prevSections) => {
      const current = prevSections.find((s) => s.id === sectionId);
      const willSelect = !current.selected;

      setWorkflow((prevWorkflow) => {
        if (willSelect) return [...prevWorkflow, current];
        return prevWorkflow.filter((w) => w.id !== sectionId);
      });

      return prevSections.map((s) =>
        s.id === sectionId ? { ...s, selected: willSelect } : s,
      );
    });
  };

  const addCustomSection = () => {
    if (!newSectionName.trim()) return;
    const newSection = {
      id: `custom-${Date.now()}`,
      title: newSectionName,
      fixed: false,
    };
    setWorkflow((prev) => [...prev, newSection]);
    setNewSectionName("");
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setWorkflow((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      const copy = [...prev];
      const [moved] = copy.splice(oldIndex, 1);
      copy.splice(newIndex, 0, moved);
      return copy;
    });
  };

  const removeSection = (section) => {
    setWorkflow((prev) => prev.filter((s) => s.id !== section.id));
    setSections((prev) =>
      prev.map((s) => (s.id === section.id ? { ...s, selected: false } : s)),
    );
  };

  const validateFieldsFunction = (required) => {
    const invalid = required?.filter((field) => field?.isValid === false);

    return invalid;
  };

  const handleSave = async () => {
    const payload = {
      ...caseTypeData,
      workflow: workflow.map((s, i) => ({ ...s, order: i + 1 })),
    };
    console.log(payload);

    const requiredFields = [
      {
        label: "Tipo de caso",
        isValid: !!caseTypeData?.case_type,
      },
      {
        label: "Empresa",
        isValid: !!caseTypeData?.company?.id,
      },
      {
        label: "Flujo de trabajo",
        isValid: workflow.length > 0,
      },
    ];

    const validate = validateFieldsFunction(requiredFields);

    const missing = validate.map((field) => `<li>${field.label}</li>`).join("");

    if (missing?.length > 0) {
      return Swal.fire({
        title: "Campos incompletos",
        icon: "error",
        confirmButtonColor: "#1ab394",
        html: `<ul style="text-align:left">${missing}</ul>`,
      });
    }

    const confirm = await questionSwal.fire({
      icon: "question",
      title: "Confirmar guardado",
      text: "¿Deseas guardar esta configuración?",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Cancelar",
    });

    if (!confirm.isConfirmed) return;

    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/casestypes/store`,
        payload,
      );

      await successSwal.fire({
        icon: "success",
        title: "Guardado exitoso",
        text: "La configuración del tipo de caso se ha guardado correctamente.",
      });

      setCaseTypeData({
        company: null,
        case_type: null,
        case_form: "",
      });

      setWorkflow([]);
      setSections(initialSections);
      setActiveStep(0);
    } catch (error) {
      console.error(error);
      await Swal.fire({
        icon: "error",
        title: "Error al guardar",
        text: error.response?.data?.message || "Ocurrió un error inesperado.",
      });
    }
  };

  const buttonStyles = {
    p: 2,
    mb: 2,
    borderRadius: 2,
    backgroundColor: "white",
  };
  const contentStyles = {
    p: 2,
    background: "white",
    my: "5px",
    borderRadius: 2,
  };
  const stylesContainer = {
    p: 2,
    mb: 2,
    borderRadius: 2,
    backgroundColor: "#f5f9ff",
  };

  return (
    <>
      <CommonSubHeader
        title="Tipos de casos"
        subTitle="Crear"
        specialRute="tiposdecasos"
      />
      <Box p={3}>
        {/* FLECHAS */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center">
            <CustomButtonPlusIcon
              disabled={activeStep === 0}
              icon={ArrowBackIosNew}
              text={"Anterior"}
              size="small"
              position={"start"}
              color={"info"}
              variant="outlined"
              handleFunction={() => setActiveStep(0)}
            />
            {activeStep === 0 && (
              <CustomButtonPlusIcon
                // disabled={activeStep === 1}
                icon={ArrowForwardIos}
                text={"Siguiente"}
                size="small"
                position={"end"}
                color={"info"}
                variant="outlined"
                handleFunction={() => setActiveStep(1)}
              />
            )}
            {activeStep === 1 && (
              <CustomButtonPlusIcon
                icon={Save}
                text={"Guardar"}
                size="small"
                position={"start"}
                color={"success"}
                variant="contained"
                handleFunction={handleSave}
              />
            )}
          </Box>
        </Paper>

        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {activeStep === 0 && (
          <Fade in timeout={800}>
            <Paper
              elevation={2}
              sx={{
                p: "10px",
                borderRadius: 2,
                backgroundColor: "#f5f9ff",
              }}>
              <Grid container sx={contentStyles}>
                {/* Empresa */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <Autocomplete
                    size="small"
                    options={companies}
                    value={caseTypeData?.company}
                    getOptionLabel={(o) => o.company_name || ""}
                    onChange={(_, v) => {
                      handleFieldChange("company", v);
                      // handleFieldChange("case_type", null);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Empresa"
                        variant="outlined"
                        sx={{ background: "#fff" }}
                      />
                    )}
                  />
                </Grid>

                {/* Tipo de caso */}
                {/* <Grid item xs={12} sx={{ mb: 2 }}>
                  <Autocomplete
                    size="small"
                    value={caseTypeData?.case_type}
                    options={casesTypes.filter(
                      (ct) => ct.company_id === caseTypeData?.company?.id,
                    )}
                    getOptionLabel={(o) => o.case_type || ""}
                    onChange={(_, v) =>
                      handleFieldChange("case_type", v || null)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de caso"
                        variant="outlined"
                        sx={{ background: "#fff" }}
                      />
                    )}
                    disabled={!caseTypeData?.company?.id} // Deshabilitado si no hay empresa seleccionada
                  />
                </Grid> */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tipo de caso"
                    variant="outlined"
                    value={caseTypeData?.case_type}
                    onChange={(e) =>
                      handleFieldChange("case_type", e.target.value)
                    }
                  />
                </Grid>
                {/* Formulario */}
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <FormControl fullWidth>
                    <FormLabel sx={{ mb: 1 }}>Formulario</FormLabel>
                    <textarea
                      style={{
                        height: "100px",
                        resize: "none",
                        padding: "6px 13px",
                        fontSize: "15px",
                        borderRadius: "4px",
                        border: "1px solid rgba(128, 128, 128, 0.35)",
                      }}
                      onChange={(event) =>
                        handleFieldChange("case_form", event.target.value)
                      }
                      value={caseTypeData?.case_form}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Fade>
        )}

        {activeStep === 1 && (
          <Fade in timeout={800}>
            <Box>
              {/* PRINCIPALES */}
              <Paper elevation={2} sx={stylesContainer}>
                <Typography variant="h6" color="#0d47a1" fontWeight={600}>
                  Secciones principales
                </Typography>
                <Box display="flex" flexDirection="row" gap={1} mt={2}>
                  {sections.map((s, i) => (
                    <FormControlLabel
                      key={s.id}
                      control={
                        <Checkbox
                          checked={s.selected}
                          onChange={() => toggleMainSection(s.id)}
                          sx={{
                            color: "#1565c0",
                            "&.Mui-checked": { color: "#0d47a1" },
                          }}
                        />
                      }
                      label={`${i + 1}. ${s.title}`}
                    />
                  ))}
                </Box>
              </Paper>

              {/* AGREGAR SECCIÓN */}
              <Paper elevation={2} sx={stylesContainer}>
                <Typography variant="h6" color="#0d47a1" fontWeight={600}>
                  Agregar sección
                </Typography>
                <Box display="flex" gap={2} mt={2}>
                  <TextField
                    size="small"
                    label="Nombre sección"
                    variant="outlined"
                    value={newSectionName}
                    onChange={(e) => setNewSectionName(e.target.value)}
                    sx={{ flex: 1, background: "#fff" }}
                  />
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#1565c0",
                      "&:hover": { background: "#0d47a1" },
                    }}
                    onClick={addCustomSection}>
                    Agregar
                  </Button>
                </Box>
              </Paper>

              {/* FLUJO DE TRABAJO */}
              <Paper elevation={2} sx={stylesContainer}>
                <Typography variant="h6" color="#0d47a1" fontWeight={600}>
                  Flujo de trabajo en expediente
                </Typography>

                <DndContext
                  collisionDetection={closestCenter}
                  onDragStart={(event) => {
                    setActiveId(event.active.id);
                  }}
                  onDragEnd={(event) => {
                    handleDragEnd(event);
                    setActiveId(null);
                  }}
                  onDragCancel={() => setActiveId(null)}>
                  <SortableContext
                    items={workflow.map((s) => s.id)}
                    strategy={rectSortingStrategy}>
                    <Box
                      display="grid"
                      // gridTemplateRows="repeat(auto-fill, minmax(30%, 5fr))"
                      // gridTemplateColumns="repeat(auto-fill, minmax(30%, 5fr))"
                      // display={"flex"}
                      gap={2}
                      mt={2}>
                      {workflow.map((section, index) => (
                        <SortableSection
                          key={section.id}
                          section={section}
                          index={index}
                          onRemove={removeSection}
                        />
                      ))}
                      <DragOverlay adjustScale={false}>
                        {activeId ? (
                          <Paper
                            sx={{
                              p: "10px 15px",
                              borderRadius: 2,
                              background: "#fff",
                              boxShadow: "0 12px 30px rgba(21,101,192,0.35)",
                            }}>
                            <Typography fontWeight={600} color="#0d47a1">
                              {workflow.find((s) => s.id === activeId)?.title}
                            </Typography>
                          </Paper>
                        ) : null}
                      </DragOverlay>
                    </Box>
                  </SortableContext>
                </DndContext>
              </Paper>
            </Box>
          </Fade>
        )}
        <Box
          sx={{
            mt: "10px",
          }}>
          <Paper elevation={2} sx={buttonStyles}>
            <ButtonGroup
              fullWidth
              variant="outlined"
              sx={{
                background: "white",
              }}>
              <Button
                color="error"
                variant="outlined"
                onClick={() => goTable("tiposdecasos", "Tipos de casos")}>
                Tabla
              </Button>
              <Button onClick={goPanel} variant="outlined">
                Inicio
              </Button>
            </ButtonGroup>
          </Paper>
        </Box>
      </Box>
    </>
  );
};

export default Crear;
